/** @format */

import {useCallback, useMemo, useState} from 'react';

import {useNavigate} from 'react-router-dom';

import {ReportsOverview} from 'ui';

import {ROUTES} from 'constants/ROUTES';

import {useProjectReportsListQuery} from '../graphql/__generated__';
import {toOverviewData, type RawOverviewReport} from './helpers/toOverviewData';

const PAGE_SIZE = 20;

/**
 * Сводный список управленческих отчётов.
 *
 * Вёрстка живёт в дизайн-системе (`ReportsOverview`), здесь остаётся то,
 * чего в ней быть не должно: запрос, перевод ответа в снимок страницы и
 * переходы. Ровно так же устроено хранилище одного проекта.
 *
 * Список общий по всем проектам: `projectReports` принимает `companyId`, но
 * не требует его.
 */
const Archive = () => {
  const navigate = useNavigate();

  // Растущий лимит вместо склейки страниц: клиент работает с no-cache, и
  // ручное накопление через fetchMore держалось бы на честном слове.
  // Строка весит несколько скалярных полей — перезапросить сорок дешевле.
  const [limit, setLimit] = useState(PAGE_SIZE);

  const {data, previousData, loading, error, refetch} = useProjectReportsListQuery({
    variables: {limit, offset: 0},
  });

  const overview = useMemo(() => {
    // Пока едет увеличенная страница, `data` пуст: при no-cache Apollo не
    // переиспользует результат, полученный на других переменных. Показываем
    // прошлый список — иначе строки мигают и подгрузка выглядит как сброс.
    const rows = ((data ?? previousData)?.currentUser?.projectReports ??
      []) as RawOverviewReport[];
    return toOverviewData(rows);
  }, [data, previousData]);

  const handleOpenReport = useCallback(
    (id: string) => navigate(`/${ROUTES.REPORT}?id=${encodeURIComponent(id)}`),
    [navigate],
  );

  const handleLogoClick = useCallback(() => navigate(ROUTES.INDEX), [navigate]);

  const handleRetry = useCallback(() => {
    void refetch();
  }, [refetch]);

  const handleLoadMore = useCallback(() => setLimit(current => current + PAGE_SIZE), []);

  return (
    <ReportsOverview
      data={overview}
      loading={loading}
      error={Boolean(error)}
      onRetry={handleRetry}
      // Страница целиком пришла — значит, вероятно, есть следующая.
      hasMore={overview.reports.length >= limit}
      onLoadMore={handleLoadMore}
      onOpenReport={handleOpenReport}
      onLogoClick={handleLogoClick}
    />
  );
};

export default Archive;
